import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { AppAbility } from '@ast-data/shared';
import {
    navigateToRestored,
    useRestoreLocation
} from '../../components/useRestoreLocation';
import {
    fieldCategoryFromKey,
    fieldCategoryPath,
    type FieldCategoryKey
} from './fieldCategories';

/** Query flag marking a visit to the picker as the user's own doing. */
const SWITCH_PARAM = 'switch';
/** The project being switched away from — highlighted in the list. */
export const FIELD_PICKER_FROM_PARAM = 'from';
/** The category to land in again, where the picked project allows it. */
export const FIELD_PICKER_CATEGORY_PARAM = 'category';

/**
 * Where every "switch project" affordance points — the drawer entry and the
 * project header alike, so there is ONE picker rather than a page and a dialog
 * that drifted apart. Landing on the picker through this path is a deliberate
 * switch, so the last project is not restored over it — and because the intent
 * is in the URL, it survives a reload and can't leak into the next launch.
 *
 * `from` carries which project to highlight and which category to stay in.
 */
export function fieldPickerPath(from?: {
    projectId: string | null;
    category: FieldCategoryKey | null;
}): string {
    const params = new URLSearchParams({ [SWITCH_PARAM]: '1' });
    if (from?.projectId) {
        params.set(FIELD_PICKER_FROM_PARAM, from.projectId);
    }
    if (from?.category) {
        params.set(FIELD_PICKER_CATEGORY_PARAM, from.category);
    }
    return `/field?${params.toString()}`;
}

/**
 * The other half of {@link fieldPickerPath}: where the user came from, for the
 * picker page and for the app bar's back button. Null when the picker was
 * reached on its own — a cold start has no project to go back to.
 */
export function useFieldPickerOrigin(ability: AppAbility): {
    projectId: string;
    category: FieldCategoryKey | null;
    /** Where "back" leads, category preserved where the project allows it. */
    path: string;
} | null {
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get(FIELD_PICKER_FROM_PARAM);
    const category = fieldCategoryFromKey(
        searchParams.get(FIELD_PICKER_CATEGORY_PARAM)
    );
    if (!projectId) {
        return null;
    }
    return {
        projectId,
        category,
        path: fieldCategoryPath(ability, projectId, category)
    };
}

/**
 * Send the user straight back to where they were last recording, when the field
 * tool opens on the picker — the installed PWA relaunches at `/field` (see the
 * manifest's `start_url`), Android ends a suspended app without warning, and a
 * crew works one Maßnahme for days.
 *
 * The deciding lives in {@link useRestoreLocation}. Returns whether that
 * decision is still pending, so the caller can hold off drawing a picker the
 * user may never see.
 */
export function useRestoreFieldProject(): boolean {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // The user asked for the picker: don't restore over it (the intent lives in
    // the URL, so it survives a reload and can never go stale).
    const switching = searchParams.get(SWITCH_PARAM) !== null;
    const target = useRestoreLocation(switching ? null : 'field');

    useEffect(() => {
        // `/field` IS this page — a restore that lands back here is the picker
        // doing its job, not a navigation.
        if (target && target !== '/field') {
            navigateToRestored(navigate, target);
        }
    }, [target, navigate]);

    return !switching && target === null;
}
